<template>
  <div>
    <v-sheet outlined rounded class="px-4 py-2">
      <div class="text-subtitle-1">{{ $t('filter_by') }}</div>
      <v-row dense>
        <v-col class="d-flex align-center" xl="3" lg="3" md="6" cols="12">
          <date-picker
            v-model="searchCriteria.timestampAfter"
            :timezone="customerTimezone"
            label="From"
            prepend-inner-icon="mdi-calendar"
            :output-moment="true"
            reset-on-timezone-change />
        </v-col>
        <v-col class="d-flex align-center" xl="3" lg="3" md="6" cols="12">
          <date-picker
            v-model="searchCriteria.timestampBefore"
            :timezone="customerTimezone"
            prepend-inner-icon="mdi-calendar"
            label="To"
            :output-moment="true"
            reset-on-timezone-change
            end-of-day />
        </v-col>
        <v-col class="d-flex align-center justify-end">
          <v-btn outlined color="primary" @click="initiateSearch()" :loading="remoteData.isBusy">
            <v-icon class="mr-1 ml-n1">mdi-magnify</v-icon>
            {{ $t('search') }}
          </v-btn>
          <v-btn outlined color="primary" class="ml-2" @click="resetTable()" :disabled="tableControlsMatchDefaults">
            <v-icon class="mr-1 ml-n1">mdi-filter-remove</v-icon>
            {{ $t('reset') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-sheet>
    <div class="d-flex justify-space-between align-center mt-2">
      <div class="text-subtitle-1 font-weight-bold hidden-md-and-down">{{ $tc('result', 2) }}</div>
      <table-pagination-controls
        :total-rows="remoteData.totalRows"
        :current-page.sync="remoteData.currentPage"
        :rows-per-page.sync="remoteData.rowsPerPage"
        @update:currentPage="refreshResults"
        @update:rowsPerPage="initiateSearch"
        :per-page-options="remoteData.pageOptions"/>
    </div>
    <div class="data-table-wrapper" :class="{'loading': remoteData.isBusy}">
      <v-data-table
        hide-default-footer
        class="vf-table mt-2"
        :headers="tableFields"
        :items="remoteData.items"
        :server-items-length="remoteData.totalRows"
        :sort-by.sync="remoteData.sortBy"
        :sort-desc.sync="remoteData.sortDesc"
        :loading="remoteData.isBusy"
        :loading-text="$t('loading')"
        @update:sort-by="initiateSearch()"
        @update:sort-desc="initiateSearch()">
        <template #item.timestamp="row">
          <multi-time-display :time="row.item.timestamp" />
        </template>
        <template #item.created="row">
          {{ getCreatedDiff(row.item) }}
        </template>
        <template #item.cooler="row">
          {{ getTemperature(row.item, 'cooler') }}
        </template>
        <template #item.freezer="row">
          {{ getTemperature(row.item, 'freezer') }}
        </template>
        <template #item.gps="row">
          <a :href="`https://maps.google.com/maps?q=${gpsCoordsAsString(row.item.telemetry)}`">
            <v-icon color="primary">mdi-map-marker</v-icon>
            {{ gpsCoordsAsString(row.item.telemetry) }}
          </a>
        </template>
        <template #item.label="row">
          <router-link :to="`/location/${row.item.location_id}`">
            {{ row.item.label }}
          </router-link>
        </template>
        <template #item.details="row">
          <v-dialog max-width="50%">
            <v-sheet class="p-4">
              <pre>{{ JSON.stringify(row.item, null, 2) }}</pre>
            </v-sheet>
            <template v-slot:activator="{ attrs, on }">
              <v-icon v-bind="attrs" v-on="on" color="primary">
                mdi-loupe
              </v-icon>
            </template>
          </v-dialog>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import DatePicker from '@/components/shared/DatePicker'
import TablePaginationControls from '@/components/shared/TablePaginationControls'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import TemperatureMixin from '@/components/mixins/TemperatureMixin'
import RemoteDataTableMixin from '@/components/mixins/RemoteDataTableMixin'
import TemperatureConverterUtil from '@/util/TemperatureConverterUtil'
import Moment from 'moment-timezone'
import { get, isEmpty } from 'lodash'

export default {
  name: 'VehicleSensorData',
  components: {
    DatePicker,
    TablePaginationControls,
    MultiTimeDisplay
  },
  mixins: [
    CurrentUserMixin,
    TemperatureMixin,
    RemoteDataTableMixin
  ],
  props: {
    vehicle: Object
  },
  data () {
    return {
      searchCriteria: this.getDefaultSearchCriteria(),
      tableFields: [
        {
          value: 'timestamp',
          text: this.$tc('timestamp'),
          sortable: false
        },
        {
          value: 'created',
          text: 'Received',
          sortable: false
        },
        {
          value: 'cooler',
          text: this.$tc('cooler'),
          sortable: false
        },
        {
          value: 'freezer',
          text: this.$tc('freezer'),
          sortable: false
        },
        {
          value: 'gps',
          text: this.$tc('gps'),
          sortable: false
        },
        {
          value: 'label',
          text: this.$tc('location'),
          sortable: false
        },
        {
          value: 'details',
          text: 'Details',
          sortable: false
        }
      ]
    }
  },
  methods: {
    gpsCoordsAsString (telemetry) {
      const lat = get(telemetry, 'lat')
      const long = get(telemetry, 'long')
      if (lat && long) {
        return `${lat.toFixed(6)},${long.toFixed(6)}`
      }
      return undefined
    },
    getCreatedDiff (item) {
      const duration = Moment(item.created).diff(item.timestamp, 'minutes')
      return duration >= 0 ? `${duration} min. later` : `${-duration} min. earlier`
    },
    getTemperature (item, zone) {
      const temperature = get(item, `telemetry.${zone}`)
      const format = this.customerContext.tempFormat()
      const converted = TemperatureConverterUtil.formatTemperature(
        TemperatureConverterUtil.to(temperature, 'C', format)
      )
      return isEmpty(converted) ? undefined : converted + '°' + format
    },
    getDefaultSearchCriteria () {
      return {
        timestampAfter: Moment().subtract(1, 'month'),
        timestampBefore: null
      }
    },
    buildSearchCriteria () {
      const searchCriteria = {
        vehicle_id: this.vehicle.id,
        page: this.remoteData.currentPage,
        'per-page': this.remoteData.rowsPerPage
      }
      if (this.searchCriteria.timestampAfter != null) {
        searchCriteria.timestamp_after = this.searchCriteria.timestampAfter.format()
      }
      if (this.searchCriteria.timestampBefore != null) {
        searchCriteria.timestamp_before = this.searchCriteria.timestampBefore.format()
      }
      return searchCriteria
    },
    /**
     * Load data from the API based on the current search criteria.
     * @return Array|null
     */
    async loadRemoteData () {
      this.remoteData.isBusy = true
      try {
        const searchCriteria = this.buildSearchCriteria()
        const result = await this.$httpAdapter.get(`/vehicle/${this.vehicle.id}/sensor-data`, {
          params: searchCriteria
        })
        this.remoteData.currentPage = Number.parseInt(get(result, 'headers.x-pagination-current-page'))
        this.remoteData.totalRows = Number.parseInt(get(result, 'headers.x-pagination-total-count'))
        this.remoteData.items = result.data
      } catch (error) {
        await this.$store.dispatch('globalMessagingModule/showModal', {
          message: this.$t('network_error_message')
        })
        throw (error)
      } finally {
        this.remoteData.isBusy = false
        this.remoteData.isLoaded = true
      }
    }
  }
}
</script>

<style scoped>

</style>
